import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  BookOutlined,
  VideoCameraOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  Col,
  Row,
  Select,
  Space,
  Table,
  Typography,
  Alert,
  message,
  Popconfirm,
  Tooltip,
  Input,
  Image as AntImage,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import { myLibraryApi, imagesApi } from "../services/api";
import type { MyLibraryEntryListItem } from "../services/api";
import { RatingDisplay } from "../components/RatingDisplay";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;
const { Search } = Input;

export function MyLibraryEntriesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate, canDelete } = usePermissions();

  const currentYear = new Date().getFullYear();

  const { data: yearsData, isLoading: yearsLoading } = useQuery({
    queryKey: ["my-library-years"],
    queryFn: myLibraryApi.listYears,
  });

  const years = yearsData?.years ?? [];

  const [year, setYear] = useState<string>(searchParams.get("year") || "all");
  const [pagination, setPagination] = useState({
    current: Number(searchParams.get("page")) || 1,
    pageSize: 100,
  });
  const [workType, setWorkType] = useState<string>(
    searchParams.get("type") || "all",
  );
  const [search, setSearch] = useState<string>(searchParams.get("search") || "");

  const yearNumber = year === "all" ? undefined : Number(year);
  const workTypeParam = workType === "all" ? undefined : workType;

  useEffect(() => {
    if (year === "all") return;
    const y = Number(year);
    if (years.length > 0 && !years.includes(y)) {
      setYear(String(years[0]));
    }
  }, [years, year]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (year === "all") {
          next.delete("year");
        } else {
          next.set("year", String(year));
        }
        next.set("page", String(pagination.current));
        next.set("type", workType);
        if (search) {
          next.set("search", search);
        } else {
          next.delete("search");
        }
        return next;
      },
      { replace: true }
    );
  }, [year, pagination.current, workType, search, setSearchParams]);

  const yearOptions = useMemo(() => {
    const set = new Set<number>([...years, currentYear]);
    const items = Array.from(set)
      .sort((a, b) => b - a)
      .map((y) => ({ value: String(y), label: y }));
    return [{ value: "all", label: "Wszystkie lata" }, ...items];
  }, [years, currentYear]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      "my-library-entries",
      yearNumber,
      workTypeParam,
      search,
      pagination.current,
      pagination.pageSize,
    ],
    queryFn: () =>
      myLibraryApi.listEntries({
        year: yearNumber,
        type: workTypeParam,
        search: search || undefined,
        page: pagination.current,
        page_size: pagination.pageSize,
      }),
  });

  const items = data?.items ?? [];

  const errorText = useMemo(() => {
    const anyErr: any = error;
    const status = anyErr?.response?.status;
    const detail = anyErr?.response?.data?.detail;
    if (status || detail) {
      return `HTTP ${status ?? "?"}${detail ? `: ${detail}` : ""}`;
    }
    return (
      anyErr?.message ?? "Sprawdź backend i konsolę przeglądarki (Network)."
    );
  }, [error]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => myLibraryApi.deleteEntry(id),
    onSuccess: () => {
      message.success("Wpis został usunięty");
      queryClient.invalidateQueries({ queryKey: ["my-library-entries"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-stats"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-monthly"] });
    },
    onError: (err: any) => {
      message.error(`Błąd usuwania: ${err.message}`);
    },
  });

  const columns: ColumnsType<MyLibraryEntryListItem> = [
    {
      title: "Data",
      dataIndex: "finished_at",
      key: "finished_at",
      width: 120,
      render: (v: string) => new Date(v).toLocaleDateString("pl-PL"),
    },
    {
      title: "",
      dataIndex: "cover_image_id",
      key: "cover_image_id",
      width: 70,
      render: (imageId: string | null, record: MyLibraryEntryListItem) => (
        <div onClick={(e) => e.stopPropagation()}>
          {imageId ? (
            <AntImage
              src={imagesApi.getFileUrl(imageId)}
              alt="Okładka"
              width={40}
              style={{
                objectFit: "cover",
                borderRadius: 4,
                aspectRatio: "2/3",
              }}
            />
          ) : (
            <div
              style={{
                width: 40,
                aspectRatio: "2/3",
                background: "#f5f5f5",
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {record.work_type === "book" ? (
                <BookOutlined style={{ color: "#d9d9d9" }} />
              ) : record.work_type === "movie" ? (
                <VideoCameraOutlined style={{ color: "#d9d9d9" }} />
              ) : (
                <PlayCircleOutlined style={{ color: "#d9d9d9" }} />
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Typ",
      dataIndex: "work_type",
      key: "work_type",
      width: 60,
      align: "center",
      render: (v: string) => {
        const label =
          v === "book" ? "Książka" : v === "movie" ? "Film" : "Serial";
        const icon =
          v === "book" ? (
            <BookOutlined style={{ color: "#52c41a" }} />
          ) : v === "movie" ? (
            <VideoCameraOutlined style={{ color: "#1677ff" }} />
          ) : (
            <PlayCircleOutlined style={{ color: "#faad14" }} />
          );
        return (
          <Tooltip title={label}>
            <span style={{ fontSize: "18px" }}>{icon}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Tytuł",
      dataIndex: "work_title",
      key: "work_title",
      render: (v: string) => <strong>{v}</strong>,
    },
    {
      title: "Platforma",
      key: "streaming_platform",
      width: 90,
      render: (_: any, record: MyLibraryEntryListItem) => {
        if (record.streaming_platform_logo_url) {
          return (
            <Tooltip title={record.streaming_platform_name}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 20,
                }}
              >
                <img
                  src={record.streaming_platform_logo_url}
                  alt={record.streaming_platform_name || "Platforma"}
                  style={{ maxHeight: 16, maxWidth: 52, objectFit: "contain" }}
                />
              </div>
            </Tooltip>
          );
        }
        if (record.streaming_platform_name) {
          return (
            <span style={{ fontSize: "11px", color: "#666" }}>
              {record.streaming_platform_name}
            </span>
          );
        }
        return "-";
      },
    },
    {
      title: "Sezon",
      dataIndex: "season_number",
      key: "season_number",
      width: 90,
      responsive: ["md"],
      render: (v: number | null, row: MyLibraryEntryListItem) =>
        row.work_type === "series" ? (v ? `S${v}` : "-") : "-",
    },
    {
      title: "Strony",
      dataIndex: "page_count",
      key: "page_count",
      width: 90,
      responsive: ["md"],
      render: (v: number | null) => (v ? v : "-"),
    },
    {
      title: "Minuty",
      dataIndex: "duration_minutes",
      key: "duration_minutes",
      width: 90,
      responsive: ["md"],
      render: (_v: number | null, row: MyLibraryEntryListItem) => {
        const minutes =
          row.work_type === "series"
            ? row.season_duration_minutes
            : row.duration_minutes;
        return minutes ? minutes : "-";
      },
    },
    {
      title: "Seans",
      key: "screening",
      width: 160,
      responsive: ["lg"],
      render: (_: any, row: MyLibraryEntryListItem) => {
        if (row.work_type !== "movie" || !row.screening_at) return "-";
        const dt = new Date(row.screening_at);
        const dateStr = dt.toLocaleDateString("pl-PL");
        const timeStr = dt.toLocaleTimeString("pl-PL", {
          hour: "2-digit",
          minute: "2-digit",
        });
        const cinema = row.cinema_name ? row.cinema_name : "";
        const hall = row.cinema_hall_name ? `, ${row.cinema_hall_name}` : "";

        return (
          <div style={{ fontSize: "12px", lineHeight: "1.2" }}>
            <div>
              {dateStr} {timeStr}
            </div>
            <div style={{ color: "#666", marginTop: "2px" }}>
              {cinema}
              {hall}
            </div>
          </div>
        );
      },
    },
    {
      title: "Ocena",
      dataIndex: "rating",
      key: "rating",
      width: 100,
      render: (v: number | null) => <RatingDisplay value={v} />,
    },
    {
      title: "Pub",
      dataIndex: "is_public",
      key: "is_public",
      width: 60,
      align: "center",
      render: (v: boolean) => (
        <Tooltip title={v ? "Wpis publiczny" : "Wpis prywatny"}>
          {v ? (
            <EyeOutlined style={{ color: "#1677ff", fontSize: "17px" }} />
          ) : (
            <EyeInvisibleOutlined style={{ color: "#bfbfbf", fontSize: "17px" }} />
          )}
        </Tooltip>
      ),
    },
    {
      title: "Akcje",
      key: "actions",
      width: 100,
      fixed: "right" as const,
      render: (_: unknown, record: MyLibraryEntryListItem) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Edytuj">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(`/my-library/edit/${record.id}`)}
            />
          </Tooltip>
          <Popconfirm
            title="Usunąć ten wpis?"
            description="Tej operacji nie można cofnąć."
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Usunąć"
            cancelText="Anuluj"
            okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
            disabled={!canDelete}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDelete}
              title={!canDelete ? "Brak uprawnień do usuwania" : "Usuń"}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/")}
          style={{ padding: 0 }}
        >
          Powrót
        </Button>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Wpisy
        </Title>
        <Button
          type="primary"
          onClick={() => navigate("/my-library/add")}
          disabled={!canCreate}
          title={!canCreate ? "Brak uprawnień do dodawania" : undefined}
        >
          Dodaj wpis
        </Button>
      </div>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Space>
              <span>Rok:</span>
              <Select
                style={{ width: 140 }}
                loading={yearsLoading}
                value={year}
                onChange={(v) => {
                  setYear(v);
                  setPagination((p) => ({ ...p, current: 1 }));
                }}
                options={yearOptions}
              />
            </Space>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Space>
              <span>Typ:</span>
              <Select
                style={{ width: 180 }}
                value={workType}
                onChange={(v) => {
                  setWorkType(v);
                  setPagination((p) => ({ ...p, current: 1 }));
                }}
                options={[
                  { value: "all", label: "Wszystkie" },
                  { value: "book", label: "Książki" },
                  { value: "movie", label: "Filmy" },
                  { value: "series", label: "Seriale" },
                ]}
              />
            </Space>
          </Col>

          <Col xs={24} sm={24} md={8}>
            <Search
              placeholder="Szukaj po tytule..."
              allowClear
              defaultValue={search}
              onSearch={(v) => {
                setSearch(v);
                setPagination((p) => ({ ...p, current: 1 }));
              }}
              style={{ width: "100%" }}
            />
          </Col>
        </Row>

        {isError ? (
          <Alert
            type="error"
            showIcon
            title="Nie udało się pobrać wpisów"
            description={errorText}
            style={{ marginBottom: 16 }}
          />
        ) : null}

        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => navigate(`/my-library/edit/${record.id}`),
            style: { cursor: "pointer" },
          })}
          locale={{ emptyText: "Brak wpisów dla wybranych filtrów." }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: ["100", "200", "500", "1000"],
            showTotal: (total: number) => `Łącznie ${total} wpisów`,
          }}
          onChange={(p) =>
            setPagination({
              current: p.current || 1,
              pageSize: p.pageSize || 50,
            })
          }
        />
      </Card>
    </div>
  );
}

export default MyLibraryEntriesPage;
